import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useBlogs, BASE_URL } from '../common/BlogContext';
import './AdminPanel.css';

// ─── Simple password gate ────────────────────────────────────────────────────
const ADMIN_PASSWORD = 'admin123'; // ← change this to your own password

function PasswordGate({ onUnlock }) {
  const [pwd, setPwd] = useState('');
  const [err, setErr] = useState(false);

  const handleLogin = () => {
    if (pwd === ADMIN_PASSWORD) {
      onUnlock();
    } else {
      setErr(true);
      setTimeout(() => setErr(false), 2000);
    }
  };

  return (
    <div className="admin-gate">
      <div className="admin-gate__card">
        <div className="admin-gate__icon">
          <i className="ti ti-lock" aria-hidden="true"></i>
        </div>
        <h2 className="admin-gate__title">Blog Admin</h2>
        <p className="admin-gate__sub">Enter password to continue</p>
        <input
          type="password"
          className={`admin-input admin-gate__input${err ? ' admin-gate__input--error' : ''}`}
          placeholder="Password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
          autoFocus
        />
        {err && <p className="admin-gate__error">Incorrect password</p>}
        <button className="admin-btn admin-btn--primary admin-gate__btn" onClick={handleLogin}>
          Login
        </button>
      </div>
    </div>
  );
}

// ─── Empty form template ─────────────────────────────────────────────────────
const EMPTY_FORM = {
  title: '',
  category: '',
  publish_date: '',
  img: '',
  body: '',
};

// ─── Main Admin Panel ─────────────────────────────────────────────────────────
function AdminPanel() {
  const { blogs, addBlog, updateBlog, deleteBlog } = useBlogs();
  const [unlocked, setUnlocked] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');
const [imgPreview, setImgPreview] = useState('');
  const fileRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);

  const [saving, setSaving] = useState(false);

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required';
    if (!form.category.trim()) e.category = 'Category is required';
if (!form.publish_date) e.publish_date = 'Date is required';
    return e;
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Format date from YYYY-MM-DD to "January 1, 2024" for display
  const formatDate = (val) => {
    if (!val) return '';
    try {
      return new Date(val).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      });
    } catch (_) { return val; }
  };

  // ── Image upload ─────────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingFile(file);
    setImgPreview(URL.createObjectURL(file));
    setForm((f) => ({ ...f, img: '' }));
  };

  const handleImgUrlChange = (e) => {
    setForm((f) => ({ ...f, img: e.target.value }));
    setImgPreview(e.target.value);
  };

  // ── CRUD ─────────────────────────────────────────────────────────────────────
const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setSaving(true);

    try {
      let imgUrl = form.img;

      if (pendingFile) {
        const fd = new FormData();
        fd.append('img', pendingFile);
        const upRes = await fetch(`${BASE_URL}/api/upload.php`, { method: 'POST', body: fd });

        const upText = await upRes.text();
        let upData;
        try { upData = JSON.parse(upText); }
        catch { throw new Error('Upload server error: ' + upText.substring(0, 200)); }
        if (!upData.success) throw new Error(upData.message || 'Image upload failed');
        imgUrl = upData.url;
        setPendingFile(null);
      }

      const payload = { ...form, img: imgUrl };

      if (editingId !== null) {
        await updateBlog(editingId, payload);
        showToast('Article updated successfully');
        setEditingId(null);
      } else {
        await addBlog(payload);
        showToast('Article added successfully');
      }
      setForm(EMPTY_FORM);
      setImgPreview('');
    } catch (err) {
      showToast(err.message || 'Error saving article.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (blog) => {
    // Try to parse display date back to YYYY-MM-DD for the date input
    let rawDate = '';
    try {
      const d = new Date(blog.publish_date);
      if (!isNaN(d)) rawDate = d.toISOString().split('T')[0];
    } catch (_) {}

    setForm({
      title: blog.title,
      category: blog.category,
publish_date: blog.publish_date,
      img: blog.img || '',
      body: blog.body || '',
    });
    setImgPreview(blog.img || '');
    setEditingId(blog.id);
    setErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id) => {
    deleteBlog(id);
    setDeleteConfirm(null);
    showToast('Article deleted', 'danger');
    if (editingId === id) handleCancel();
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setImgPreview('');
    setEditingId(null);
    setPendingFile(null);  // ← add this line
    setErrors({});
  };

  const filtered = blogs.filter(
    (b) =>
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      (b.category || '').toLowerCase().includes(search.toLowerCase())
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="admin-page">

      {/* ── Toast notification ── */}
      {toast && (
        <div className={`admin-toast admin-toast--${toast.type}`}>
          <i className={`ti ti-${toast.type === 'success' ? 'circle-check' : 'trash'}`} aria-hidden="true"></i>
          {toast.msg}
        </div>
      )}

      {/* ── Delete confirm modal ── */}
      {deleteConfirm && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal">
            <i className="ti ti-alert-triangle admin-modal__icon" aria-hidden="true"></i>
            <p className="admin-modal__text">Delete this article?</p>
            <p className="admin-modal__sub">This action cannot be undone.</p>
            <div className="admin-modal__actions">
              <button className="admin-btn admin-btn--ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="admin-btn admin-btn--danger" onClick={() => handleDelete(deleteConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sidebar ── */}
      <div className="admin-page__sidebar">
        <div className="admin-sidebar__header">
          <div className="admin-sidebar__logo">
            <i className="ti ti-news" aria-hidden="true"></i>
          </div>
          <div>
            <p className="admin-sidebar__title">Blog Admin</p>
            <p className="admin-sidebar__sub">{blogs.length} articles</p>
          </div>
        </div>
        <Link to="/blog" className="admin-sidebar__view-link">
          <i className="ti ti-external-link" aria-hidden="true"></i> View Public Blog
        </Link>
        <Link to="/" className="admin-sidebar__view-link" style={{ marginTop: 8 }}>
          <i className="ti ti-home" aria-hidden="true"></i> Back to Website
        </Link>
      </div>

      {/* ── Main area ── */}
      <div className="admin-page__main">

        {/* ── Form card ── */}
        <div className="admin-form-card">
          <h2 className="admin-form-card__heading">
            {editingId !== null ? (
              <><i className="ti ti-edit" aria-hidden="true"></i> Edit Article</>
            ) : (
              <><i className="ti ti-plus" aria-hidden="true"></i> New Article</>
            )}
          </h2>

          <div className="admin-form__grid">

            {/* Title */}
            <div className={`admin-field admin-field--full${errors.title ? ' admin-field--error' : ''}`}>
              <label className="admin-label">Title *</label>
              <input
                type="text"
                className="admin-input"
                placeholder="e.g. Top 10 Wooden Architecture Buildings"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              {errors.title && <span className="admin-error">{errors.title}</span>}
            </div>

            {/* Category */}
            <div className={`admin-field${errors.category ? ' admin-field--error' : ''}`}>
              <label className="admin-label">Category / Tags *</label>
              <input
                type="text"
                className="admin-input"
                placeholder="e.g. inspiration, Architecture"
                value={form.category}
onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
              {errors.category && <span className="admin-error">{errors.category}</span>}
            </div>

            {/* Date — proper date picker */}
            <div className={`admin-field${errors.publish_date ? ' admin-field--error' : ''}`}>
              <label className="admin-label">Publish Date *</label>
              <input
                type="date"
                className="admin-input"
                value={form.publish_date}
onChange={(e) => setForm({ ...form, publish_date: e.target.value })}
              />
              {errors.publish_date && <span className="admin-error">{errors.publish_date}</span>}
            </div>

            {/* Image — URL input + file upload */}
            <div className="admin-field admin-field--full">
              <label className="admin-label">
                Cover Image <span className="admin-label__opt">(optional)</span>
              </label>
              <div className="admin-img-row">
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Paste image URL or upload a file →"
                  value={form.img.startsWith('data:') ? '' : form.img}
                  onChange={handleImgUrlChange}
                />
                <button
                  className="admin-btn admin-btn--ghost admin-upload-btn"
                  onClick={() => fileRef.current.click()}
                  type="button"
                >
                  <i className="ti ti-upload" aria-hidden="true"></i> Upload
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>
              {imgPreview && (
                <div className="admin-img-preview-wrap">
                  <img src={imgPreview} alt="preview" className="admin-img-preview" />
                  <button
                    className="admin-img-preview__remove"
                    onClick={() => { setImgPreview(''); setForm((f) => ({ ...f, img: '' })); }}
                  >
                    <i className="ti ti-x" aria-hidden="true"></i>
                  </button>
                </div>
              )}
            </div>

            {/* Body / Content */}
            <div className="admin-field admin-field--full">
              <label className="admin-label">
                Article Content <span className="admin-label__opt">(optional)</span>
              </label>
              <textarea
                className="admin-input admin-textarea"
                placeholder="Write the full article content here..."
                rows={8}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
              />
              <span className="admin-char-count">{form.body.length} characters</span>
            </div>

          </div>

          <div className="admin-form__actions">
            {editingId !== null && (
              <button className="admin-btn admin-btn--ghost" onClick={handleCancel}>
                Cancel
              </button>
            )}
            <button className="admin-btn admin-btn--primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : editingId !== null ? 'Save Changes' : 'Add Article'}
            </button>
          </div>
        </div>

        {/* ── Articles table ── */}
        <div className="admin-table-card">
          <div className="admin-table-card__header">
            <h2 className="admin-form-card__heading" style={{ margin: 0 }}>
              <i className="ti ti-list" aria-hidden="true"></i> All Articles
            </h2>
            <div className="admin-search-wrap">
              <i className="ti ti-search admin-search-icon" aria-hidden="true"></i>
              <input
                type="text"
                className="admin-input admin-search-input"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="admin-empty">
              <i className="ti ti-article-off" style={{ fontSize: 32 }} aria-hidden="true"></i>
              <p>No articles found.</p>
            </div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Cover</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((blog) => (
                    <tr key={blog.id} className={editingId === blog.id ? 'admin-table__row--active' : ''}>
                      <td>
                        {blog.img ? (
                          <img src={blog.img} alt="" className="admin-table__thumb" />
                        ) : (
                          <div className="admin-table__thumb admin-table__thumb--empty">
                            <i className="ti ti-photo-off" aria-hidden="true"></i>
                          </div>
                        )}
                      </td>
                      <td className="admin-table__title-cell">{blog.title}</td>
                      <td>
                        <div className="admin-table__tags">
                          {blog.category.split(/[,\s]+/).filter(Boolean).map((t, i) => (
                            <span key={i} className="admin-table__tag">{t}</span>
                          ))}
                        </div>
                      </td>
                      <td className="admin-table__date">{blog.publish_date}</td>
                      <td>
                        <div className="admin-table__actions">
                          <button
                            className="admin-icon-btn admin-icon-btn--edit"
                            onClick={() => handleEdit(blog)}
                            title="Edit"
                          >
                            <i className="ti ti-edit" aria-hidden="true"></i>
                          </button>
                          <button
                            className="admin-icon-btn admin-icon-btn--delete"
                            onClick={() => setDeleteConfirm(blog.id)}
                            title="Delete"
                          >
                            <i className="ti ti-trash" aria-hidden="true"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;